import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDateLocale } from '@/hooks/useDateLocale';
import {
  Loader2, Calendar, Gauge, Fuel, Thermometer, Zap, Satellite, Signal, DoorOpen,
  Route, Clock, Activity, Info, Car, Battery, Cog, Droplets, Wind, ChevronDown,
  Play, Pause, SkipForward, SkipBack, Navigation, MapPin, User, AlertTriangle, Timer, Wifi,
  PanelRightOpen, PanelRightClose, Download,
} from 'lucide-react';
import * as XLSX from 'xlsx';
import { format, startOfDay, endOfDay, formatDuration, intervalToDuration } from 'date-fns';
import { DateRange } from 'react-day-picker';
import {
  RadialBarChart, RadialBar, PolarAngleAxis,
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, ReferenceLine,
} from 'recharts';

import { DashboardLayout } from '@/components/dashboard/DashboardLayout';
import { useTraccarSession } from '@/hooks/useTraccarSession';
import { RouteReplayMap } from '@/components/history/RouteReplayMap';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  fetchDevices,
  fetchPositions,
  fetchTrips,
  fetchStops,
  fetchSummary,
  fetchRoute,
  type Device,
  type Position,
  type Trip,
  type Stop,
  type Summary,
} from '@/lib/traccar-api';
import { cn } from '@/lib/utils';
import * as React from "react";

// ---------------------------------------------------------------------------
// Ruptela Smart5 IO → Traccar attribute mapping helpers
// Traccar's RuptelaProtocolDecoder maps certain IO IDs to named attributes
// with unit conversions. Unmapped IOs appear as "io{ID}" in attributes.
//
// Key Traccar mappings for Ruptela:
//   IO 29  → power        (×0.001 → V)
//   IO 30  → battery      (×0.001 → V)
//   IO 94  → rpm          (×0.25)
//   IO 197 → rpm          (×0.125)
//   IO 98  → fuel         (×100/255 → %)
//   IO 207 → fuelLevel    (×0.4 → %)
//   IO 205 → fuelLevel    (raw)
//   IO 208 → fuelUsed     (×0.5 → L)
//   IO 251/409 → ignition (boolean)
//   IO 65  → odometer     (m)
//   IO 645 → obdOdometer  (×1000 → m)
//   IO 95  → obdSpeed     (km/h)
// Unmapped but relevant Smart5 IOs:
//   IO 27  → io27  GSM/UMTS signal level (0-31)
//   IO 115 → io115 CAN engine coolant temp (°C, 128=88°C)
//   IO 96  → io96  OBD engine coolant temp (°C)
//   IO 116 → io116 CAN fuel rate (L/h, 600=30L/h)
//   IO 114 → io114 CAN total vehicle distance (m, 10000=50000m)
//   IO 203 → io203 CAN engine hours (h, 200000=10000h)
//   IO 204 → io204 CAN service distance (km)
//   IO 210 → io210 CAN wheel based speed (km/h, 15360=60km/h)
//   IO 201 → io201 CAN AdBlue level (%)
//   IO 366 → io366 CAN selected gear
//   IO 367 → io367 CAN current gear
//   IO 362 → io362 CAN parking brake (0/1)
//   IO 89  → io89  CAN ambient air temp (°C, 9376=20°C)
//   IO 522 → io522 CAN ignition (0/1)
//   IO 39  → engineLoad (%)
//   IO 920 → io920 Vehicle RPM (combined OBD+LCV)
//   IO 923 → io923 Vehicle Fuel Level % (combined)
//   IO 926 → io926 Vehicle Coolant Temp (combined)
// ---------------------------------------------------------------------------

// Ruptela error/not-available sentinel values per byte-size:
//   1-byte: 255 (0xFF)
//   2-byte: 65535 (0xFFFF)
//   4-byte: 4294967295 (0xFFFFFFFF)
const RUPTELA_ERROR_VALUES = new Set([255, 65535, 4294967295]);

/** Read the first defined numeric value from a list of attribute keys.
 *  Skips Ruptela error sentinel values (255, 65535, etc.). */
function attrNum(attrs: Record<string, unknown>, ...keys: string[]): number | undefined {
  for (const k of keys) {
    const v = attrs[k];
    if (v !== undefined && v !== null && v !== '') {
      const n = Number(v);
      if (!isNaN(n) && !RUPTELA_ERROR_VALUES.has(n)) return n;
    }
  }
  return undefined;
}

/** Read first defined value (any type) */
function attrAny(attrs: Record<string, unknown>, ...keys: string[]): unknown {
  for (const k of keys) {
    if (attrs[k] !== undefined && attrs[k] !== null) return attrs[k];
  }
  return undefined;
}

/**
 * Read a CAN/OBD temperature value with J1939 SPN offset conversion.
 * Many Ruptela CAN temp IOs use a -40°C offset (SPN 110, 171, 172).
 * Traccar-mapped attributes (coolantTemp, engineTemp, oilTemperature) may already be converted,
 * while raw io{ID} values need the offset applied.
 *
 * Priority: Traccar-mapped names first (already converted), then raw IOs (need offset).
 */
function attrTemp(attrs: Record<string, unknown>): number | undefined {
  // Traccar-mapped names — already in °C (no offset needed)
  for (const k of ['coolantTemp', 'engineTemp', 'oilTemperature', 'oiltemperature']) {
    const v = attrs[k];
    if (v !== undefined && v !== null) {
      const n = Number(v);
      if (!isNaN(n) && !RUPTELA_ERROR_VALUES.has(n)) return n;
    }
  }
  // Combined OBD+LCV (io926) — already converted by device firmware
  const combined = attrs['io926'];
  if (combined !== undefined && combined !== null) {
    const n = Number(combined);
    if (!isNaN(n) && !RUPTELA_ERROR_VALUES.has(n)) return n;
  }
  // Raw CAN/OBD IOs — need -40 offset (J1939 SPN 110 / OBD PID 0x05)
  for (const k of ['io96', 'io115']) {
    const v = attrs[k];
    if (v !== undefined && v !== null) {
      const n = Number(v);
      if (!isNaN(n) && !RUPTELA_ERROR_VALUES.has(n)) return n - 40;
    }
  }
  return undefined;
}

/**
 * Read CAN ambient air temperature with J1939 offset.
 * SPN 171: offset -273°C, resolution 0.03125°C/bit
 * But Ruptela doc says io89: "9376=20°C" → value × 0.03125 - 273
 * io97 OBD: standard OBD PID 0x46, offset -40°C
 * io927 combined: already converted
 */
function attrAmbientTemp(attrs: Record<string, unknown>): number | undefined {
  // Combined (already converted)
  const combined = attrs['io927'];
  if (combined !== undefined && combined !== null) {
    const n = Number(combined);
    if (!isNaN(n) && !RUPTELA_ERROR_VALUES.has(n)) return n;
  }
  // CAN ambient (SPN 171): raw × 0.03125 - 273
  const can = attrs['io89'];
  if (can !== undefined && can !== null) {
    const n = Number(can);
    if (!isNaN(n) && !RUPTELA_ERROR_VALUES.has(n) && n !== 65535) return n * 0.03125 - 273;
  }
  // OBD ambient (PID 0x46): raw - 40
  const obd = attrs['io97'];
  if (obd !== undefined && obd !== null) {
    const n = Number(obd);
    if (!isNaN(n) && !RUPTELA_ERROR_VALUES.has(n)) return n - 40;
  }
  return undefined;
}

const getUtcReportRange = (range?: DateRange) => {
  if (!range?.from || !range?.to) return null;
  return {
    from: startOfDay(range.from).toISOString(),
    to: endOfDay(range.to).toISOString(),
  };
};

// --- Gauge Component ---
interface GaugeCardProps {
  label: string;
  value: number;
  max: number;
  unit: string;
  color: string;
  icon: React.ReactNode;
  onClick?: () => void;
  chartAxis?: 'left' | 'right';
}

const GaugeCard = ({ label, value, max, unit, color, icon, onClick, chartAxis }: GaugeCardProps) => {
  const percentage = max > 0 ? Math.min((value / max) * 100, 100) : 0;
  const data = [{ value: percentage, fill: color }];

  return (
    <Card className={cn("transition-colors", onClick && "cursor-pointer hover:ring-1 hover:ring-primary/30", chartAxis && "ring-1 ring-primary/50")} onClick={onClick}>
      <CardContent className="pt-2 pb-2 px-3 flex flex-col items-center relative">
        {chartAxis && (
          <span className="absolute top-1 right-1.5 text-[8px] font-bold text-primary/70">{chartAxis === 'left' ? 'L' : 'R'}</span>
        )}
        <div className="relative w-[80px] h-[80px]">
          <RadialBarChart
            width={80}
            height={80}
            cx={40}
            cy={40}
            innerRadius={28}
            outerRadius={38}
            barSize={7}
            data={data}
            startAngle={225}
            endAngle={-45}
          >
            <PolarAngleAxis type="number" domain={[0, 100]} angleAxisId={0} tick={false} />
            <RadialBar
              background={{ fill: 'hsl(var(--muted))' }}
              dataKey="value"
              angleAxisId={0}
              cornerRadius={4}
            />
          </RadialBarChart>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className="text-sm font-bold">{Math.round(value)}</span>
            <span className="text-[9px] text-muted-foreground">{unit}</span>
          </div>
        </div>
        <div className="flex items-center gap-1 mt-0.5">
          {icon}
          <span className="text-[10px] font-medium text-muted-foreground">{label}</span>
        </div>
      </CardContent>
    </Card>
  );
};

// --- Status Indicator Card ---
interface StatusCardProps {
  label: string;
  value: string | number;
  icon: React.ReactNode;
  active?: boolean;
  color?: string;
  onClick?: () => void;
  chartAxis?: 'left' | 'right';
}

const StatusCard = ({ label, value, icon, active, color, onClick, chartAxis }: StatusCardProps) => (
  <Card className={cn("transition-colors", active === false && "opacity-60", onClick && "cursor-pointer hover:ring-1 hover:ring-primary/30", chartAxis && "ring-1 ring-primary/50")} onClick={onClick}>
    <CardContent className="py-1.5 px-2.5 flex items-center gap-2">
      <div className={cn(
        "p-1.5 rounded-md",
        color || (active ? "bg-green-500/10 text-green-500" : "bg-muted text-muted-foreground")
      )}>
        {React.cloneElement(icon as React.ReactElement, { className: 'h-3.5 w-3.5' })}
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[10px] text-muted-foreground truncate leading-tight">{label}</p>
        <p className="text-xs font-semibold truncate leading-tight">{value}</p>
      </div>
      {chartAxis && (
        <span className="text-[8px] font-bold text-primary/70 shrink-0">{chartAxis === 'left' ? 'L' : 'R'}</span>
      )}
    </CardContent>
  </Card>
);

// --- Main Page ---
const PerformanceReport = () => {
  const { t } = useTranslation();
  const dateFnsLocale = useDateLocale();
  const debugMode = new URLSearchParams(window.location.search).get('debug') === '1';
  const { isAuthenticated } = useTraccarSession();

  const [devices, setDevices] = useState<Device[]>([]);
  const [positions, setPositions] = useState<Position[]>([]);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [trips, setTrips] = useState<Trip[]>([]);
  const [stops, setStops] = useState<Stop[]>([]);
  const [selectedTrip, setSelectedTrip] = useState<Trip | null>(null);
  const [selectedStop, setSelectedStop] = useState<Stop | null>(null);
  const [fullRoute, setFullRoute] = useState(false);
  const [showTripsPanel, setShowTripsPanel] = useState(true);
  const [routePositions, setRoutePositions] = useState<Position[]>([]);
  const [isLoadingRoute, setIsLoadingRoute] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [selectedDeviceId, setSelectedDeviceId] = useState<number | null>(null);
  const [dateRange, setDateRange] = useState<DateRange | undefined>({
    from: startOfDay(new Date()),
    to: endOfDay(new Date()),
  });

  // Chart series visibility: key → 'left' | 'right', absent = hidden
  const [seriesAxis, setSeriesAxis] = useState<Record<string, 'left' | 'right'>>({ speed: 'left', altitude: 'right' });

  // Cycle series axis: off → left → right → off
  const cycleSeries = useCallback((key: string) => {
    setSeriesAxis(prev => {
      const current = prev[key];
      if (!current) return { ...prev, [key]: 'left' };
      if (current === 'left') return { ...prev, [key]: 'right' };
      const { [key]: _, ...rest } = prev;
      return rest;
    });
  }, []);

  // Playback state
  const [currentIndex, setCurrentIndex] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const playIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Load devices and positions on mount
  const loadInitialData = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      setIsLoading(true);
      const [devicesData, positionsData] = await Promise.all([
        fetchDevices(),
        fetchPositions(),
      ]);
      setDevices(devicesData);
      setPositions(positionsData);
      if (!selectedDeviceId && devicesData.length > 0) {
        setSelectedDeviceId(devicesData[0].id);
      }
      setHasLoaded(true);
    } catch (error) {
      console.error('Failed to load initial data:', error);
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, selectedDeviceId]);

  // Load report data when device or date changes
  const loadReportData = useCallback(async () => {
    const reportRange = getUtcReportRange(dateRange);
    if (!selectedDeviceId || !reportRange) return;
    try {
      setIsLoadingData(true);
      const [summaryData, tripsData, stopsData] = await Promise.all([
        fetchSummary([selectedDeviceId], reportRange.from, reportRange.to),
        fetchTrips(selectedDeviceId, reportRange.from, reportRange.to),
        fetchStops(selectedDeviceId, reportRange.from, reportRange.to),
      ]);

      setSummary(summaryData.length > 0 ? summaryData[0] : null);
      setTrips(tripsData);
      setStops(stopsData);
      setSelectedTrip(null);
      setSelectedStop(null);
      setFullRoute(false);
    } catch (error) {
      console.error('Failed to load report data:', error);
      setSummary(null);
      setTrips([]);
      setStops([]);
      setSelectedStop(null);
      setFullRoute(false);
    } finally {
      setIsLoadingData(false);
    }
  }, [selectedDeviceId, dateRange]);

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  useEffect(() => {
    if (selectedDeviceId && dateRange?.from && dateRange?.to) {
      loadReportData();
    }
  }, [selectedDeviceId, dateRange, loadReportData]);

  // Refresh positions periodically for live gauges
  useEffect(() => {
    const interval = setInterval(async () => {
      try {
        const positionsData = await fetchPositions();
        setPositions(positionsData);
      } catch {
        // silent fail
      }
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  // Fetch route positions when a trip, stop, or full route is selected
  const selectedTimeRange = useMemo(() => {
    if (fullRoute && selectedDeviceId && dateRange?.from && dateRange?.to) {
      const reportRange = getUtcReportRange(dateRange);
      if (!reportRange) return null;
      return { deviceId: selectedDeviceId, from: reportRange.from, to: reportRange.to };
    }
    if (selectedTrip) return { deviceId: selectedTrip.deviceId, from: selectedTrip.startTime, to: selectedTrip.endTime };
    if (selectedStop) return { deviceId: selectedStop.deviceId, from: selectedStop.startTime, to: selectedStop.endTime };
    return null;
  }, [fullRoute, selectedDeviceId, dateRange, selectedTrip, selectedStop]);

  useEffect(() => {
    if (!selectedTimeRange) {
      setRoutePositions([]);
      return;
    }
    let cancelled = false;
    const load = async () => {
      setIsLoadingRoute(true);
      try {
        const route = await fetchRoute(selectedTimeRange.deviceId, selectedTimeRange.from, selectedTimeRange.to);
        if (!cancelled) setRoutePositions(route);
      } catch (error) {
        console.error('Failed to fetch route:', error);
        if (!cancelled) setRoutePositions([]);
      } finally {
        if (!cancelled) setIsLoadingRoute(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [selectedTimeRange]);

  // Build telemetry chart data from route positions
  const telemetryData = useMemo(() => {
    if (routePositions.length === 0) return [];
    return routePositions.map((pos, idx) => {
      const a = pos.attributes || {};
      const rpmVal = attrNum(a, 'rpm', 'io920', 'io94', 'io197');
      const tempVal = attrTemp(a);
      const fuelVal = attrNum(a, 'fuel', 'fuelLevel', 'io923', 'io98', 'io207');
      const throttleVal = attrNum(a, 'throttle', 'io928', 'io206', 'io103');
      const fuelConsVal = attrNum(a, 'fuelConsumption', 'io925', 'io100');
      const fuelRateRawVal = attrNum(a, 'io116');
      const fuelRateVal = fuelRateRawVal !== undefined ? fuelRateRawVal * 0.05 : undefined;
      const ecoBrakVal = attrNum(a, 'io402');
      const ecoAccVal = attrNum(a, 'io403');
      const ecoCorVal = attrNum(a, 'io404');
      const ignVal = attrAny(a, 'ignition', 'io522', 'in4');
      const powerVal = attrNum(a, 'power', 'io29');
      const battVal = attrNum(a, 'battery', 'io30');
      const satVal = pos.attributes?.sat ?? attrNum(a, 'sat');
      const gsmVal = attrNum(a, 'rssi', 'io27');
      const loadVal = attrNum(a, 'engineLoad', 'io39', 'io519');
      const adblueVal = attrNum(a, 'io201', 'io357');
      const gearVal = attrNum(a, 'io367', 'io366');
      const fuelUsedVal = attrNum(a, 'fuelUsed', 'io208', 'io92');
      const odomVal = attrNum(a, 'obdOdometer', 'io645', 'odometer', 'totalDistance', 'io114');
      const odomKm922 = attrNum(a, 'io922');
      const odomKm = odomKm922 ?? (odomVal !== undefined ? odomVal / 1000 : undefined);
      const engHoursRaw = attrNum(a, 'io203');
      const engHoursVal = engHoursRaw !== undefined ? (engHoursRaw > 100000 ? engHoursRaw / 3600000 : engHoursRaw) : undefined;
      const svcDistVal = attrNum(a, 'io204');
      const parkBrakeVal = attrNum(a, 'io362');
      const milDistVal = attrNum(a, 'io102');
      const engRuntimeVal = attrNum(a, 'io107');
      const doorVal = attrAny(a, 'door', 'io369', 'io518');
      const ambientVal = (() => {
        const c = a['io927']; if (c !== undefined) { const n = Number(c); if (!isNaN(n) && !RUPTELA_ERROR_VALUES.has(n)) return n; }
        const can = a['io89']; if (can !== undefined) { const n = Number(can); if (!isNaN(n) && !RUPTELA_ERROR_VALUES.has(n)) return n * 0.03125 - 273; }
        const obd = a['io97']; if (obd !== undefined) { const n = Number(obd); if (!isNaN(n) && !RUPTELA_ERROR_VALUES.has(n)) return n - 40; }
        return undefined;
      })();
      return {
        idx,
        time: new Date(pos.fixTime).getTime(),
        timeLabel: format(new Date(pos.fixTime), 'HH:mm:ss'),
        speed: Math.round(pos.speed * 1.852),
        altitude: Math.round(pos.altitude),
        ...(rpmVal !== undefined && { rpm: Math.round(rpmVal) }),
        ...(tempVal !== undefined && { temp: Math.round(tempVal) }),
        ...(fuelVal !== undefined && { fuel: Math.round(fuelVal) }),
        ...(throttleVal !== undefined && { throttle: Math.round(throttleVal) }),
        ...(fuelConsVal !== undefined && { fuelCons: Math.round(fuelConsVal * 10) / 10 }),
        ...(fuelRateVal !== undefined && { fuelRate: Math.round(fuelRateVal * 10) / 10 }),
        ...(ecoBrakVal !== undefined && { ecoBraking: Math.round(ecoBrakVal) / 100 }),
        ...(ecoAccVal !== undefined && { ecoAccel: Math.round(ecoAccVal) / 100 }),
        ...(ecoCorVal !== undefined && { ecoCornering: Math.round(ecoCorVal) / 100 }),
        ...(ignVal !== undefined && { ignition: ignVal ? 1 : 0 }),
        ...(powerVal !== undefined && { power: Math.round(powerVal * 10) / 10 }),
        ...(battVal !== undefined && { battery: Math.round(battVal * 100) / 100 }),
        ...(satVal !== undefined && { satellites: Number(satVal) }),
        ...(gsmVal !== undefined && { gsm: Math.round(Number(gsmVal) / 31 * 100) }),
        ...(loadVal !== undefined && { engineLoad: Math.round(loadVal) }),
        ...(adblueVal !== undefined && { adblue: Math.round(adblueVal) }),
        ...(gearVal !== undefined && { gear: Number(gearVal) }),
        ...(fuelUsedVal !== undefined && { fuelUsed: Math.round(fuelUsedVal * 10) / 10 }),
        ...(ambientVal !== undefined && { ambient: Math.round(ambientVal) }),
        ...(odomKm !== undefined && { odometer: Math.round(odomKm) }),
        ...(engHoursVal !== undefined && { engineHours: Math.round(engHoursVal * 10) / 10 }),
        ...(svcDistVal !== undefined && { serviceDistance: Math.round(svcDistVal) }),
        ...(parkBrakeVal !== undefined && { parkingBrake: parkBrakeVal }),
        ...(milDistVal !== undefined && { milDistance: Math.round(milDistVal) }),
        ...(engRuntimeVal !== undefined && { engineRuntime: Math.round(engRuntimeVal / 60) }),
        ...(doorVal !== undefined && { door: doorVal ? 1 : 0 }),
      };
    });
  }, [routePositions]);

  // Detect which extra metrics are available in the route
  const hasRpm = useMemo(() => telemetryData.some(d => d.rpm !== undefined), [telemetryData]);
  const hasTemp = useMemo(() => telemetryData.some(d => d.temp !== undefined), [telemetryData]);
  const hasFuel = useMemo(() => telemetryData.some(d => d.fuel !== undefined), [telemetryData]);
  const hasThrottle = useMemo(() => telemetryData.some(d => d.throttle !== undefined), [telemetryData]);
  const hasFuelCons = useMemo(() => telemetryData.some(d => d.fuelCons !== undefined), [telemetryData]);
  const hasFuelRate = useMemo(() => telemetryData.some(d => d.fuelRate !== undefined), [telemetryData]);
  const hasEcoBraking = useMemo(() => telemetryData.some(d => d.ecoBraking !== undefined), [telemetryData]);
  const hasEcoAccel = useMemo(() => telemetryData.some(d => d.ecoAccel !== undefined), [telemetryData]);
  const hasEcoCornering = useMemo(() => telemetryData.some(d => d.ecoCornering !== undefined), [telemetryData]);
  const hasIgnition = useMemo(() => telemetryData.some(d => d.ignition !== undefined), [telemetryData]);
  const hasPower = useMemo(() => telemetryData.some(d => d.power !== undefined), [telemetryData]);
  const hasBattery = useMemo(() => telemetryData.some(d => d.battery !== undefined), [telemetryData]);
  const hasSatellites = useMemo(() => telemetryData.some(d => d.satellites !== undefined), [telemetryData]);
  const hasGsm = useMemo(() => telemetryData.some(d => d.gsm !== undefined), [telemetryData]);
  const hasEngineLoad = useMemo(() => telemetryData.some(d => d.engineLoad !== undefined), [telemetryData]);
  const hasAdblue = useMemo(() => telemetryData.some(d => d.adblue !== undefined), [telemetryData]);
  const hasGear = useMemo(() => telemetryData.some(d => d.gear !== undefined), [telemetryData]);
  const hasFuelUsed = useMemo(() => telemetryData.some(d => d.fuelUsed !== undefined), [telemetryData]);
  const hasAmbient = useMemo(() => telemetryData.some(d => d.ambient !== undefined), [telemetryData]);
  const hasOdometer = useMemo(() => telemetryData.some(d => d.odometer !== undefined), [telemetryData]);
  const hasEngineHours = useMemo(() => telemetryData.some(d => d.engineHours !== undefined), [telemetryData]);
  const hasServiceDistance = useMemo(() => telemetryData.some(d => d.serviceDistance !== undefined), [telemetryData]);
  const hasParkingBrake = useMemo(() => telemetryData.some(d => d.parkingBrake !== undefined), [telemetryData]);
  const hasMilDistance = useMemo(() => telemetryData.some(d => d.milDistance !== undefined), [telemetryData]);
  const hasEngineRuntime = useMemo(() => telemetryData.some(d => d.engineRuntime !== undefined), [telemetryData]);
  const hasDoor = useMemo(() => telemetryData.some(d => d.door !== undefined), [telemetryData]);

  // Reset playback when trip changes
  useEffect(() => {
    setCurrentIndex(0);
    setPlaying(false);
  }, [selectedTrip]);

  // Playback interval
  useEffect(() => {
    if (playing && routePositions.length > 0) {
      playIntervalRef.current = setInterval(() => {
        setCurrentIndex((prev) => {
          if (prev >= routePositions.length - 1) {
            setPlaying(false);
            return prev;
          }
          return prev + 1;
        });
      }, 500 / playbackSpeed);
    }
    return () => {
      if (playIntervalRef.current) {
        clearInterval(playIntervalRef.current);
        playIntervalRef.current = null;
      }
    };
  }, [playing, playbackSpeed, routePositions.length]);

  const handlePlayPause = () => {
    if (routePositions.length === 0) return;
    if (currentIndex >= routePositions.length - 1) {
      setCurrentIndex(0);
      setPlaying(true);
    } else {
      setPlaying(!playing);
    }
  };

  const handleStepForward = () => {
    if (currentIndex < routePositions.length - 1) {
      setPlaying(false);
      setCurrentIndex(currentIndex + 1);
    }
  };

  const handleStepBackward = () => {
    if (currentIndex > 0) {
      setPlaying(false);
      setCurrentIndex(currentIndex - 1);
    }
  };

  const currentReplayPos = routePositions[currentIndex] || null;

  const selectedDevice = useMemo(
    () => devices.find(d => d.id === selectedDeviceId),
    [devices, selectedDeviceId]
  );

  const latestPosition = useMemo(
    () => positions.find(p => p.deviceId === selectedDeviceId),
    [positions, selectedDeviceId]
  );

  // Use replay position for gauges/status when playing back a trip
  const activePosition = currentReplayPos || latestPosition;
  const activePositionDateTime = activePosition
    ? format(new Date(activePosition.fixTime), 'MMM d, yyyy HH:mm:ss', { locale: dateFnsLocale })
    : null;
  const attrs = activePosition?.attributes || {};

  // ---- Gauge values ----
  // Attribute fallback chains cover both protocols:
  //   Ruptela: raw io{ID} keys needing conversion
  //   Teltonika: Traccar-decoded standard attribute names
  //
  // Speed: position.speed (knots→km/h). Traccar always provides this.
  const speedKmh = (activePosition?.speed || 0) * 1.852;

  // RPM:  Teltonika AVL 36/85 → "rpm"  |  Ruptela IO 94/197 → "rpm", IO 920 combined
  const rpm = attrNum(attrs, 'rpm', 'io920', 'io94', 'io197') ?? 0;

  // Fuel level %:  Teltonika AVL 84→"fuel" (×0.1), AVL 89→"fuelLevel"
  //                Ruptela  IO 98→"fuel" (×100/255), IO 207→"fuelLevel" (×0.4), IO 923 combined
  const fuelLevel = attrNum(attrs, 'fuel', 'fuelLevel', 'io923', 'io98', 'io207') ?? 0;

  // Temperature:   Teltonika AVL 32→"coolantTemp", AVL 115→"engineTemp" (already °C), oilTemperature
  //               Ruptela   raw io96/io115 need -40°C J1939 offset; 255 = not available
  const coolantTemp = attrTemp(attrs) ?? 0;

  // ---- Status values ----
  // Ignition:  Teltonika AVL 239→"ignition"  |  Ruptela IO 251/409→"ignition", IO 522, DIN4
  const ignRaw = attrAny(attrs, 'ignition', 'io522', 'in4');
  const ignition = ignRaw !== undefined ? Boolean(Number(ignRaw)) : undefined;

  // Power supply:  Teltonika AVL 66→"power" (V)  |  Ruptela IO 29→"power" (V)
  const powerV = attrNum(attrs, 'power', 'io29') ?? 0;
  // Internal battery:  Teltonika AVL 67→"battery" (V)  |  Ruptela IO 30→"battery" (V)
  const batteryV = attrNum(attrs, 'battery', 'io30') ?? 0;
  // Battery level %:  Teltonika AVL 113→"batteryLevel"
  const batteryLevelPct = attrNum(attrs, 'batteryLevel');

  // Satellites
  const satellites = attrNum(attrs, 'sat') ?? 0;

  // GSM signal:
  //   Teltonika AVL 21→"rssi" (0-5 scale: 0=none … 5=excellent)
  //   Ruptela   IO 27→"io27"  (0-31 scale: 0=none … 31=excellent)
  // Normalise to percentage based on detected range.
  const gsmRaw = attrNum(attrs, 'rssi', 'io27') ?? 0;
  const gsmPercent = (() => {
    if (gsmRaw <= 0) return 0;
    if (gsmRaw <= 5) return Math.round((gsmRaw / 5) * 100);   // Teltonika 0-5
    if (gsmRaw <= 31) return Math.round((gsmRaw / 31) * 100);  // Ruptela 0-31
    return gsmRaw; // already percentage or raw dBm
  })();

  // Door:  Teltonika AVL 662→"door"  |  Ruptela io369 CAN door, io518 LCV doors
  const doorRaw = attrAny(attrs, 'door', 'io369', 'io518');
  const door = doorRaw !== undefined ? Boolean(Number(doorRaw)) : undefined;

  // Odometer:  Teltonika AVL 16→"odometer" (m), AVL 87→"obdOdometer"
  //            Ruptela   IO 65→"odometer" (m), io114 CAN (m), io645→obdOdometer, io922 (km)
  const odometerM = attrNum(attrs, 'obdOdometer', 'io645', 'odometer', 'totalDistance', 'io114');
  const odometerKm922 = attrNum(attrs, 'io922');
  const odometer = odometerKm922 ?? (odometerM !== undefined ? odometerM / 1000 : 0);

  // Fuel consumption:
  //   Teltonika AVL 13→"fuelConsumption" (×0.01, L/100km), AVL 110 (×0.1)
  //   Ruptela   IO 100→"fuelConsumption" (÷20 L/h), io116 CAN, io925 combined
  // Unit is ambiguous — show as-is with generic label
  const fuelConsumption = attrNum(attrs, 'fuelConsumption', 'io925', 'io100');

  // Total fuel used:  Teltonika AVL 83/107→"fuelUsed"  |  Ruptela IO 208→"fuelUsed", io92 CAN
  const totalFuelUsed = attrNum(attrs, 'fuelUsed', 'io208', 'io92');

  // Engine load:  Teltonika AVL 31→"engineLoad"  |  Ruptela IO 39→"engineLoad", io519
  const engineLoad = attrNum(attrs, 'engineLoad', 'io39', 'io519');

  // Throttle / accelerator pedal:
  //   Teltonika AVL 82→"throttle" (already %)
  //   Ruptela   IO 206→io206 CAN pedal (×0.4 → %), IO 103→io103 OBD pedal (×100/255 → %), IO 928 combined
  const throttleRaw = attrNum(attrs, 'throttle', 'io928');
  const throttleCan = attrNum(attrs, 'io206');
  const throttleObd = attrNum(attrs, 'io103');
  const throttle = throttleRaw ?? (throttleCan !== undefined ? throttleCan * 0.4 : undefined) ?? (throttleObd !== undefined ? throttleObd * (100 / 255) : undefined);

  // AdBlue level:  Ruptela io201 (%), io357 DEF (%)
  const adblueLevel = attrNum(attrs, 'io201', 'io357');

  // Service distance:  Ruptela io204 (km)
  const serviceDistance = attrNum(attrs, 'io204');

  // Current gear:  Ruptela io367 CAN, io366 selected gear
  const currentGear = attrNum(attrs, 'io367', 'io366');

  // Parking brake:  Ruptela io362 (0=not set, 1=set)
  const parkingBrake = attrNum(attrs, 'io362');

  // Ambient air temp:  Ruptela io927 combined, io89 CAN SPN 171, io97 OBD
  const ambientTemp = attrAmbientTemp(attrs);

  // Motion: io13/io173 → "motion"
  const motion = attrAny(attrs, 'motion');

  // Driver ID:  Ruptela iButton / RFID → "driverUniqueId"
  const driverUniqueId = attrAny(attrs, 'driverUniqueId') as string | undefined;

  // MIL distance:  Ruptela io102 → OBD distance traveled while MIL activated (km)
  const milDistance = attrNum(attrs, 'io102');

  // Engine runtime:  Ruptela io107 → OBD time since engine start (seconds)
  const engineRuntime = attrNum(attrs, 'io107');

  // CAN fuel rate:  Ruptela io116 (×0.05 → L/h)
  const fuelRateRaw = attrNum(attrs, 'io116');
  const fuelRate = fuelRateRaw !== undefined ? fuelRateRaw * 0.05 : undefined;

  // Network technology:  Ruptela io762
  const networkTypeRaw = attrNum(attrs, 'io762');
  const networkType = (() => {
    if (networkTypeRaw === undefined) return undefined;
    const map: Record<number, string> = { 0: '2G', 2: '3G', 7: 'LTE Cat1', 8: 'CatM1', 9: 'NB-IoT', 10: 'LTE' };
    return map[networkTypeRaw] ?? `${networkTypeRaw}`;
  })();

  // ECO driving:  Ruptela io402/403/404 (×0.01 → m/s²)
  const ecoBrakingRaw = attrNum(attrs, 'io402');
  const ecoBraking = ecoBrakingRaw !== undefined ? ecoBrakingRaw * 0.01 : undefined;
  const ecoAccelRaw = attrNum(attrs, 'io403');
  const ecoAccel = ecoAccelRaw !== undefined ? ecoAccelRaw * 0.01 : undefined;
  const ecoCorneringRaw = attrNum(attrs, 'io404');
  const ecoCornering = ecoCorneringRaw !== undefined ? ecoCorneringRaw * 0.01 : undefined;

  const formatTripDuration = (ms: number) => {
    const duration = intervalToDuration({ start: 0, end: ms });
    return formatDuration(duration, { format: ['hours', 'minutes'], zero: false, locale: dateFnsLocale }) || '< 1 min';
  };

  const exportToXls = useCallback(() => {
    const wb = XLSX.utils.book_new();
    const deviceName = selectedDevice?.name ?? 'device';
    const dateLabel = dateRange?.from ? format(dateRange.from, 'yyyy-MM-dd') : 'report';

    // Summary sheet
    if (summary) {
      const summaryRows = [
        { Field: t('performance.totalDistance'), Value: `${(summary.distance / 1000).toFixed(1)} km` },
        { Field: t('performance.trips'), Value: trips.length },
        { Field: t('performance.engineHours'), Value: summary.engineHours ? formatTripDuration(summary.engineHours) : '—' },
        { Field: t('performance.fuelConsumed'), Value: summary.spentFuel ? `${summary.spentFuel.toFixed(1)} L` : '—' },
        { Field: t('performance.maxSpeed'), Value: `${Math.round(summary.maxSpeed * 1.852)} km/h` },
      ];
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(summaryRows), t('performance.tripSummary'));
    }

    // Trips sheet
    if (trips.length > 0) {
      const tripsRows = trips.map(trip => ({
        [t('performance.start')]: format(new Date(trip.startTime), 'yyyy-MM-dd HH:mm:ss'),
        [t('performance.end')]: format(new Date(trip.endTime), 'HH:mm:ss'),
        [`${t('performance.totalDistance')} (km)`]: (trip.distance / 1000).toFixed(1),
        [`${t('performance.maxSpeed')} (km/h)`]: Math.round(trip.maxSpeed * 1.852),
        [`${t('performance.averageSpeed')} (km/h)`]: trip.averageSpeed ? Math.round(trip.averageSpeed * 1.852) : '',
        [t('performance.duration')]: formatTripDuration(trip.duration),
      }));
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(tripsRows), t('performance.trips'));
    }

    // Stops sheet
    if (stops.length > 0) {
      const stopsRows = stops.map(stop => ({
        [t('performance.start')]: format(new Date(stop.startTime), 'yyyy-MM-dd HH:mm:ss'),
        [t('performance.end')]: format(new Date(stop.endTime), 'HH:mm:ss'),
        [t('performance.duration')]: formatTripDuration(stop.duration),
        [t('performance.address')]: stop.address ?? '',
      }));
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(stopsRows), t('performance.stops'));
    }

    // Telemetry sheet (if route is loaded)
    if (telemetryData.length > 0) {
      const telemetryRows = telemetryData.map(d => ({
        [t('performance.time')]: d.timeLabel,
        [`${t('performance.speed')} (km/h)`]: d.speed,
        [`${t('performance.altitude')} (m)`]: d.altitude,
        ...(d.rpm !== undefined && { [`${t('performance.rpm')} (rpm)`]: d.rpm }),
        ...(d.temp !== undefined && { [`${t('performance.coolantTemp')} (°C)`]: d.temp }),
        ...(d.fuel !== undefined && { [`${t('performance.fuelLevel')} (%)`]: d.fuel }),
        ...(d.throttle !== undefined && { [`${t('performance.throttle')} (%)`]: d.throttle }),
        ...(d.engineLoad !== undefined && { [`${t('performance.engineLoad')} (%)`]: d.engineLoad }),
        ...(d.fuelCons !== undefined && { [t('performance.fuelConsumption')]: d.fuelCons }),
        ...(d.fuelRate !== undefined && { [`${t('performance.fuelRate')} (L/h)`]: d.fuelRate }),
        ...(d.fuelUsed !== undefined && { [`${t('performance.totalFuelUsed')} (L)`]: d.fuelUsed }),
        ...(d.adblue !== undefined && { [`${t('performance.adblueLevel')} (%)`]: d.adblue }),
        ...(d.gear !== undefined && { [t('performance.gear')]: d.gear }),
        ...(d.power !== undefined && { [`${t('performance.powerSupply')} (V)`]: d.power }),
        ...(d.battery !== undefined && { [`${t('performance.battery')} (V)`]: d.battery }),
        ...(d.satellites !== undefined && { [t('performance.satellites')]: d.satellites }),
        ...(d.gsm !== undefined && { [`${t('performance.gsmSignal')} (%)`]: d.gsm }),
        ...(d.ambient !== undefined && { [`${t('performance.ambientTemp')} (°C)`]: d.ambient }),
        ...(d.odometer !== undefined && { [`${t('performance.odometer')} (km)`]: d.odometer }),
        ...(d.engineHours !== undefined && { [`${t('performance.engineHours')} (h)`]: d.engineHours }),
        ...(d.ecoBraking !== undefined && { [`${t('performance.ecoBraking')} (m/s²)`]: d.ecoBraking }),
        ...(d.ecoAccel !== undefined && { [`${t('performance.ecoAccel')} (m/s²)`]: d.ecoAccel }),
        ...(d.ecoCornering !== undefined && { [`${t('performance.ecoCornering')} (m/s²)`]: d.ecoCornering }),
        ...(d.ignition !== undefined && { [t('performance.ignition')]: d.ignition }),
        ...(d.door !== undefined && { [t('performance.door')]: d.door }),
      }));
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(telemetryRows), t('performance.telemetry'));
    }

    XLSX.writeFile(wb, `performance_${deviceName}_${dateLabel}.xlsx`);
  }, [selectedDevice, dateRange, summary, trips, stops, telemetryData, t]);

  const tripTotals = useMemo(() => {
    return trips.reduce(
      (acc, trip) => ({
        distance: acc.distance + (trip.distance || 0),
        duration: acc.duration + (trip.duration || 0),
        tripCount: acc.tripCount + 1,
        maxSpeed: Math.max(acc.maxSpeed, trip.maxSpeed || 0),
        avgSpeed: acc.avgSpeed + (trip.averageSpeed || 0),
      }),
      { distance: 0, duration: 0, tripCount: 0, maxSpeed: 0, avgSpeed: 0 }
    );
  }, [trips]);

  // Merge trips and stops into a chronological timeline
  type TimelineItem = { type: 'trip'; data: Trip } | { type: 'stop'; data: Stop };
  const timeline = useMemo<TimelineItem[]>(() => {
    const items: TimelineItem[] = [
      ...trips.map(t => ({ type: 'trip' as const, data: t })),
      ...stops.map(s => ({ type: 'stop' as const, data: s })),
    ];
    items.sort((a, b) => new Date(a.data.startTime).getTime() - new Date(b.data.startTime).getTime());
    return items;
  }, [trips, stops]);

  if (isLoading && !hasLoaded) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-12 w-12 animate-spin text-primary" />
          <p className="text-muted-foreground">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  const headerContent = (
    <div className="flex items-center gap-3 flex-wrap">
      <Select
        value={selectedDeviceId?.toString() ?? ""}
        onValueChange={(value) => setSelectedDeviceId(parseInt(value, 10))}
      >
        <SelectTrigger className="w-[180px]">
          <SelectValue placeholder={t('performance.selectDevice')} />
        </SelectTrigger>
        <SelectContent>
          {devices.map((device) => (
            <SelectItem key={device.id} value={device.id.toString()}>
              {device.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Popover>
        <PopoverTrigger asChild>
          <Button variant="outline" size="sm" className="gap-2">
            <Calendar className="h-3.5 w-3.5" />
            {dateRange?.from ? (
              dateRange.to ? (
                <>
                  {format(dateRange.from, "MMM d", { locale: dateFnsLocale })} - {format(dateRange.to, "MMM d, yyyy", { locale: dateFnsLocale })}
                </>
              ) : (
                format(dateRange.from, "MMM d, yyyy", { locale: dateFnsLocale })
              )
            ) : (
              t('performance.selectDates')
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <CalendarComponent
            mode="range"
            selected={dateRange}
            onSelect={setDateRange}
            numberOfMonths={2}
            disabled={(date) => date > new Date()}
          />
        </PopoverContent>
      </Popover>

      <Button
        variant="outline"
        size="sm"
        className="gap-2"
        onClick={exportToXls}
        disabled={!summary && trips.length === 0 && telemetryData.length === 0}
      >
        <Download className="h-3.5 w-3.5" />
        XLS
      </Button>

      <div className="hidden sm:flex items-center gap-2 ml-1 text-xs text-muted-foreground">
        <span>{selectedDevice?.uniqueId || ''}</span>
        {latestPosition?.protocol && (
          <span>{latestPosition.protocol}</span>
        )}
        {motion !== undefined && (
          <Badge variant="outline" className="text-[10px]">
            {Number(motion) ? t('performance.moving') : t('performance.stationary')}
          </Badge>
        )}
        <Badge variant={selectedDevice?.status === 'online' ? 'default' : 'secondary'} className={cn(
          "text-[10px]",
          selectedDevice?.status === 'online' && 'bg-green-500 hover:bg-green-600'
        )}>
          {selectedDevice?.status || 'unknown'}
        </Badge>
        {selectedDevice?.lastUpdate && (
          <span>{format(new Date(selectedDevice.lastUpdate), 'MMM d, HH:mm:ss', { locale: dateFnsLocale })}</span>
        )}
      </div>
    </div>
  );

  // Build the list of extra CAN/OBD status cards — always show all, greyed out when undefined
  const gearLabel = currentGear !== undefined
    ? (currentGear === 0 ? 'N' : currentGear === 251 ? 'P' : currentGear < 0 ? `R${Math.abs(currentGear)}` : `${currentGear}`)
    : undefined;

  const extraCards: StatusCardProps[] = [
    { label: t('performance.engineLoad'), value: engineLoad !== undefined ? `${Math.round(engineLoad)}%` : '—', icon: <Activity className="h-4 w-4" />, active: engineLoad !== undefined && engineLoad > 0, color: engineLoad !== undefined ? (engineLoad > 80 ? "bg-red-500/10 text-red-500" : "bg-blue-500/10 text-blue-500") : undefined, onClick: () => cycleSeries('engineLoad'), chartAxis: seriesAxis['engineLoad'] },
    { label: t('performance.throttle'), value: throttle !== undefined ? `${Math.round(throttle)}%` : '—', icon: <Gauge className="h-4 w-4" />, active: throttle !== undefined && throttle > 0, color: throttle !== undefined ? "bg-orange-500/10 text-orange-500" : undefined, onClick: () => cycleSeries('throttle'), chartAxis: seriesAxis['throttle'] },
    { label: t('performance.fuelConsumption'), value: fuelConsumption !== undefined ? `${fuelConsumption.toFixed(1)}` : '—', icon: <Fuel className="h-4 w-4" />, active: fuelConsumption !== undefined && fuelConsumption > 0, color: fuelConsumption !== undefined ? "bg-orange-500/10 text-orange-500" : undefined, onClick: () => cycleSeries('fuelCons'), chartAxis: seriesAxis['fuelCons'] },
    { label: t('performance.totalFuelUsed'), value: totalFuelUsed !== undefined ? `${totalFuelUsed.toFixed(0)} L` : '—', icon: <Fuel className="h-4 w-4" />, active: totalFuelUsed !== undefined, onClick: () => cycleSeries('fuelUsed'), chartAxis: seriesAxis['fuelUsed'] },
    { label: t('performance.fuelRate'), value: fuelRate !== undefined ? `${fuelRate.toFixed(1)} L/h` : '—', icon: <Fuel className="h-4 w-4" />, active: fuelRate !== undefined && fuelRate > 0, color: fuelRate !== undefined ? "bg-amber-500/10 text-amber-500" : undefined, onClick: () => cycleSeries('fuelRate'), chartAxis: seriesAxis['fuelRate'] },
    { label: t('performance.adblueLevel'), value: adblueLevel !== undefined ? `${Math.round(adblueLevel)}%` : '—', icon: <Droplets className="h-4 w-4" />, active: adblueLevel !== undefined && adblueLevel > 15, color: adblueLevel !== undefined ? (adblueLevel > 15 ? "bg-blue-500/10 text-blue-500" : "bg-red-500/10 text-red-500") : undefined, onClick: () => cycleSeries('adblue'), chartAxis: seriesAxis['adblue'] },
    { label: t('performance.engineRuntime'), value: engineRuntime !== undefined ? `${Math.round(engineRuntime / 60)} min` : '—', icon: <Timer className="h-4 w-4" />, active: engineRuntime !== undefined && engineRuntime > 0, color: engineRuntime !== undefined ? "bg-blue-500/10 text-blue-500" : undefined, onClick: () => cycleSeries('engineRuntime'), chartAxis: seriesAxis['engineRuntime'] },
    { label: t('performance.gear'), value: gearLabel ?? '—', icon: <Cog className="h-4 w-4" />, active: currentGear !== undefined && currentGear !== 0, color: currentGear !== undefined ? "bg-purple-500/10 text-purple-500" : undefined, onClick: () => cycleSeries('gear'), chartAxis: seriesAxis['gear'] },
    { label: t('performance.serviceDistance'), value: serviceDistance !== undefined ? `${Math.round(serviceDistance)} km` : '—', icon: <Cog className="h-4 w-4" />, active: serviceDistance !== undefined && serviceDistance > 0, color: serviceDistance !== undefined ? (serviceDistance > 0 ? "bg-green-500/10 text-green-500" : "bg-red-500/10 text-red-500") : undefined, onClick: () => cycleSeries('serviceDistance'), chartAxis: seriesAxis['serviceDistance'] },
    { label: t('performance.parkingBrake'), value: parkingBrake !== undefined ? (parkingBrake === 1 ? t('performance.engaged') : t('performance.released')) : '—', icon: <Car className="h-4 w-4" />, active: parkingBrake !== undefined && parkingBrake === 0, color: parkingBrake !== undefined ? (parkingBrake === 1 ? "bg-red-500/10 text-red-500" : "bg-green-500/10 text-green-500") : undefined, onClick: () => cycleSeries('parkingBrake'), chartAxis: seriesAxis['parkingBrake'] },
    { label: t('performance.ambientTemp'), value: ambientTemp !== undefined ? `${Math.round(ambientTemp)}°C` : '—', icon: <Wind className="h-4 w-4" />, active: ambientTemp !== undefined, color: ambientTemp !== undefined ? "bg-cyan-500/10 text-cyan-500" : undefined, onClick: () => cycleSeries('ambient'), chartAxis: seriesAxis['ambient'] },
    { label: t('performance.driver'), value: driverUniqueId ? String(driverUniqueId) : '—', icon: <User className="h-4 w-4" />, active: !!driverUniqueId, color: driverUniqueId ? "bg-indigo-500/10 text-indigo-500" : undefined },
    { label: t('performance.milDistance'), value: milDistance !== undefined ? `${milDistance} km` : '—', icon: <AlertTriangle className="h-4 w-4" />, active: milDistance !== undefined && milDistance > 0, color: milDistance !== undefined && milDistance > 0 ? "bg-red-500/10 text-red-500" : undefined, onClick: () => cycleSeries('milDistance'), chartAxis: seriesAxis['milDistance'] },
    { label: t('performance.networkType'), value: networkType ?? '—', icon: <Wifi className="h-4 w-4" />, active: networkType !== undefined, color: networkType !== undefined ? "bg-sky-500/10 text-sky-500" : undefined },
    { label: t('performance.ecoBraking'), value: ecoBraking !== undefined ? `${ecoBraking.toFixed(2)} m/s²` : '—', icon: <Activity className="h-4 w-4" />, active: ecoBraking !== undefined, color: ecoBraking !== undefined ? "bg-emerald-500/10 text-emerald-500" : undefined, onClick: () => cycleSeries('ecoBraking'), chartAxis: seriesAxis['ecoBraking'] },
    { label: t('performance.ecoAccel'), value: ecoAccel !== undefined ? `${ecoAccel.toFixed(2)} m/s²` : '—', icon: <Activity className="h-4 w-4" />, active: ecoAccel !== undefined, color: ecoAccel !== undefined ? "bg-emerald-500/10 text-emerald-500" : undefined, onClick: () => cycleSeries('ecoAccel'), chartAxis: seriesAxis['ecoAccel'] },
    { label: t('performance.ecoCornering'), value: ecoCornering !== undefined ? `${ecoCornering.toFixed(2)} m/s²` : '—', icon: <Activity className="h-4 w-4" />, active: ecoCornering !== undefined, color: ecoCornering !== undefined ? "bg-emerald-500/10 text-emerald-500" : undefined, onClick: () => cycleSeries('ecoCornering'), chartAxis: seriesAxis['ecoCornering'] },
    { label: t('performance.door'), value: door !== undefined ? (door ? t('performance.doorOpen') : t('performance.doorClosed')) : '—', icon: <DoorOpen className="h-4 w-4" />, active: door !== undefined && !door, color: door !== undefined ? (door ? "bg-red-500/10 text-red-500" : "bg-green-500/10 text-green-500") : undefined, onClick: () => cycleSeries('door'), chartAxis: seriesAxis['door'] },
  ];

  // Non-zero visible attributes count for the raw section
  const attrEntries = Object.entries(attrs).sort(([a], [b]) => a.localeCompare(b));

  return (
    <DashboardLayout headerContent={headerContent}>
      <div className="h-full flex flex-col">
      {/* Playback bar — pinned above scrollable content */}
      {(selectedTrip || selectedStop || fullRoute) && routePositions.length > 0 && (
        <div className="shrink-0 z-30 bg-background/80 backdrop-blur-md border-b px-6 py-2">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1.5">
              <Button
                variant="outline"
                size="icon"
                className="h-7 w-7"
                onClick={handleStepBackward}
                disabled={currentIndex === 0}
              >
                <SkipBack className="h-3 w-3" />
              </Button>
              <Button
                variant="default"
                size="icon"
                onClick={handlePlayPause}
                className="h-8 w-8"
              >
                {playing ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}
              </Button>
              <Button
                variant="outline"
                size="icon"
                className="h-7 w-7"
                onClick={handleStepForward}
                disabled={currentIndex >= routePositions.length - 1}
              >
                <SkipForward className="h-3 w-3" />
              </Button>
            </div>
            <Slider
              value={[currentIndex]}
              min={0}
              max={Math.max(0, routePositions.length - 1)}
              step={1}
              onValueChange={([val]) => {
                setPlaying(false);
                setCurrentIndex(val);
              }}
              className="flex-1"
            />
            <div className="flex items-center gap-2 shrink-0">
              <span className="text-xs text-muted-foreground tabular-nums min-w-[9.5rem] text-center">
                {activePositionDateTime
                  ? activePositionDateTime
                  : `${currentIndex + 1}/${routePositions.length}`}
              </span>
              <div className="flex items-center gap-1">
                {[1, 2, 5, 10].map((speed) => (
                  <Button
                    key={speed}
                    variant={playbackSpeed === speed ? "default" : "outline"}
                    size="sm"
                    className="h-6 px-1.5 text-[10px]"
                    onClick={() => setPlaybackSpeed(speed)}
                  >
                    {speed}x
                  </Button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
      <div className="p-6 flex-1 min-h-0 flex flex-col">
        <div className={cn("grid gap-6 flex-1 min-h-0", showTripsPanel ? "grid-cols-1 lg:grid-cols-[1fr_320px]" : "grid-cols-1")}>
          {/* Left column: all data */}
          <div className="flex flex-col gap-3 min-w-0 min-h-0">
            {/* Gauges Row */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <GaugeCard
                label={t('performance.speed')}
                value={speedKmh}
                max={200}
                unit="km/h"
                color="hsl(221, 83%, 53%)"
                icon={<Gauge className="h-3.5 w-3.5 text-blue-500" />}
                onClick={() => cycleSeries('speed')}
                chartAxis={seriesAxis['speed']}
              />
              <GaugeCard
                label={t('performance.rpm')}
                value={rpm}
                max={8000}
                unit="rpm"
                color="hsl(262, 83%, 58%)"
                icon={<Activity className="h-3.5 w-3.5 text-purple-500" />}
                onClick={() => cycleSeries('rpm')}
                chartAxis={seriesAxis['rpm']}
              />
              <GaugeCard
                label={t('performance.fuelLevel')}
                value={fuelLevel}
                max={100}
                unit="%"
                color="hsl(142, 76%, 36%)"
                icon={<Fuel className="h-3.5 w-3.5 text-green-600" />}
                onClick={() => cycleSeries('fuel')}
                chartAxis={seriesAxis['fuel']}
              />
              <GaugeCard
                label={t('performance.coolantTemp')}
                value={coolantTemp}
                max={130}
                unit="°C"
                color="hsl(0, 84%, 60%)"
                icon={<Thermometer className="h-3.5 w-3.5 text-red-500" />}
                onClick={() => cycleSeries('temp')}
                chartAxis={seriesAxis['temp']}
              />
            </div>

            {/* Vehicle Status Indicators */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
              <StatusCard
                label={t('performance.ignition')}
                value={ignition === undefined ? '—' : ignition ? t('performance.on') : t('performance.off')}
                icon={<Zap className="h-4 w-4" />}
                active={ignition === true}
                onClick={() => cycleSeries('ignition')}
                chartAxis={seriesAxis['ignition']}
              />
              <StatusCard
                label={t('performance.powerSupply')}
                value={powerV ? `${Number(powerV).toFixed(1)}V` : '—'}
                icon={<Battery className="h-4 w-4" />}
                active={powerV > 11.5}
                color={powerV > 11.5 ? "bg-green-500/10 text-green-500" : powerV > 0 ? "bg-yellow-500/10 text-yellow-500" : undefined}
                onClick={() => cycleSeries('power')}
                chartAxis={seriesAxis['power']}
              />
              <StatusCard
                label={t('performance.battery')}
                value={batteryLevelPct !== undefined ? `${Math.round(batteryLevelPct)}%` : batteryV ? `${Number(batteryV).toFixed(2)}V` : '—'}
                icon={<Battery className="h-4 w-4" />}
                active={batteryLevelPct !== undefined ? batteryLevelPct > 20 : batteryV > 3.3}
                color={
                  batteryLevelPct !== undefined
                    ? (batteryLevelPct > 20 ? "bg-green-500/10 text-green-500" : "bg-yellow-500/10 text-yellow-500")
                    : (batteryV > 3.3 ? "bg-green-500/10 text-green-500" : batteryV > 0 ? "bg-yellow-500/10 text-yellow-500" : undefined)
                }
                onClick={() => cycleSeries('battery')}
                chartAxis={seriesAxis['battery']}
              />
              <StatusCard
                label={t('performance.satellites')}
                value={satellites || '—'}
                icon={<Satellite className="h-4 w-4" />}
                active={satellites >= 4}
                color={satellites >= 4 ? "bg-green-500/10 text-green-500" : satellites > 0 ? "bg-yellow-500/10 text-yellow-500" : undefined}
                onClick={() => cycleSeries('satellites')}
                chartAxis={seriesAxis['satellites']}
              />
              <StatusCard
                label={t('performance.gsmSignal')}
                value={gsmPercent > 0 ? `${gsmPercent}%` : '—'}
                icon={<Signal className="h-4 w-4" />}
                active={gsmPercent > 50}
                color={gsmPercent > 50 ? "bg-green-500/10 text-green-500" : gsmPercent > 0 ? "bg-yellow-500/10 text-yellow-500" : undefined}
                onClick={() => cycleSeries('gsm')}
                chartAxis={seriesAxis['gsm']}
              />
              <StatusCard
                label={t('performance.odometer')}
                value={odometer > 0 ? `${odometer.toFixed(0)} km` : '—'}
                icon={<Route className="h-4 w-4" />}
                active={odometer > 0}
                onClick={() => cycleSeries('odometer')}
                chartAxis={seriesAxis['odometer']}
              />
            </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                {extraCards.map((card) => (
                  <StatusCard key={card.label} {...card} />
                ))}
              </div>


            {/* Route Map + Chart side by side */}
            <div className={cn("grid gap-4 flex-1 min-h-0", (selectedTrip || selectedStop || fullRoute) ? "grid-cols-1 lg:grid-cols-2" : "grid-cols-1")}>
            <Card className="min-h-0 flex flex-col">
              <CardContent className="p-0 flex-1 min-h-0">
                <RouteReplayMap
                  positions={routePositions}
                  currentIndex={currentIndex}
                  onIndexChange={(idx) => {
                    setPlaying(false);
                    setCurrentIndex(idx);
                  }}
                  className="h-full"
                  playing={playing}
                  onPlayPause={handlePlayPause}
                  onStepForward={handleStepForward}
                  onStepBackward={handleStepBackward}
                  playbackSpeed={playbackSpeed}
                  onSpeedChange={setPlaybackSpeed}
                />
              </CardContent>
            </Card>

            {/* Trip Telemetry Charts */}
            {(selectedTrip || selectedStop || fullRoute) && (
              <Card className="min-h-0 flex flex-col">
                <CardContent className="pt-3 pb-3 px-3 flex-1 min-h-0 flex flex-col">
                  {isLoadingRoute ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                    </div>
                  ) : telemetryData.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <Activity className="h-10 w-10 mx-auto mb-3 opacity-50" />
                      <p className="text-sm">{t('performance.noTelemetry')}</p>
                    </div>
                  ) : (() => {
                    const allSeries = [
                      { key: 'speed', label: t('performance.speed'), unit: ' km/h', color: 'hsl(221, 83%, 53%)', available: true },
                      { key: 'altitude', label: t('performance.altitude'), unit: ' m', color: 'hsl(142, 76%, 36%)', available: true },
                      { key: 'rpm', label: t('performance.rpm'), unit: ' rpm', color: 'hsl(262, 83%, 58%)', available: hasRpm },
                      { key: 'temp', label: t('performance.temp'), unit: ' °C', color: 'hsl(0, 84%, 60%)', available: hasTemp },
                      { key: 'fuel', label: t('performance.fuel'), unit: '%', color: 'hsl(45, 93%, 47%)', available: hasFuel },
                      { key: 'throttle', label: t('performance.throttle'), unit: '%', color: 'hsl(25, 95%, 53%)', available: hasThrottle },
                      { key: 'fuelCons', label: t('performance.fuelConsumption'), unit: ' L/100km', color: 'hsl(25, 80%, 50%)', available: hasFuelCons },
                      { key: 'fuelRate', label: t('performance.fuelRate'), unit: ' L/h', color: 'hsl(35, 90%, 50%)', available: hasFuelRate },
                      { key: 'ecoBraking', label: t('performance.ecoBraking'), unit: ' m/s²', color: 'hsl(0, 70%, 50%)', available: hasEcoBraking },
                      { key: 'ecoAccel', label: t('performance.ecoAccel'), unit: ' m/s²', color: 'hsl(120, 60%, 40%)', available: hasEcoAccel },
                      { key: 'ecoCornering', label: t('performance.ecoCornering'), unit: ' m/s²', color: 'hsl(280, 70%, 55%)', available: hasEcoCornering },
                      { key: 'ignition', label: t('performance.ignition'), unit: '', color: 'hsl(50, 90%, 50%)', available: hasIgnition },
                      { key: 'power', label: t('performance.powerSupply'), unit: ' V', color: 'hsl(160, 60%, 45%)', available: hasPower },
                      { key: 'battery', label: t('performance.battery'), unit: ' V', color: 'hsl(200, 70%, 50%)', available: hasBattery },
                      { key: 'satellites', label: t('performance.satellites'), unit: '', color: 'hsl(190, 80%, 42%)', available: hasSatellites },
                      { key: 'gsm', label: t('performance.gsmSignal'), unit: '%', color: 'hsl(210, 60%, 55%)', available: hasGsm },
                      { key: 'engineLoad', label: t('performance.engineLoad'), unit: '%', color: 'hsl(240, 60%, 55%)', available: hasEngineLoad },
                      { key: 'fuelUsed', label: t('performance.totalFuelUsed'), unit: ' L', color: 'hsl(30, 80%, 50%)', available: hasFuelUsed },
                      { key: 'adblue', label: t('performance.adblueLevel'), unit: '%', color: 'hsl(195, 80%, 50%)', available: hasAdblue },
                      { key: 'gear', label: t('performance.gear'), unit: '', color: 'hsl(270, 50%, 55%)', available: hasGear },
                      { key: 'ambient', label: t('performance.ambientTemp'), unit: ' °C', color: 'hsl(180, 50%, 45%)', available: hasAmbient },
                      { key: 'odometer', label: t('performance.odometer'), unit: ' km', color: 'hsl(140, 40%, 50%)', available: hasOdometer },
                      { key: 'engineHours', label: t('performance.engineHours'), unit: ' h', color: 'hsl(40, 60%, 50%)', available: hasEngineHours },
                      { key: 'serviceDistance', label: t('performance.serviceDistance'), unit: ' km', color: 'hsl(100, 50%, 45%)', available: hasServiceDistance },
                      { key: 'parkingBrake', label: t('performance.parkingBrake'), unit: '', color: 'hsl(350, 70%, 50%)', available: hasParkingBrake },
                      { key: 'milDistance', label: t('performance.milDistance'), unit: ' km', color: 'hsl(10, 80%, 50%)', available: hasMilDistance },
                      { key: 'engineRuntime', label: t('performance.engineRuntime'), unit: ' min', color: 'hsl(220, 50%, 55%)', available: hasEngineRuntime },
                      { key: 'door', label: t('performance.door'), unit: '', color: 'hsl(340, 70%, 50%)', available: hasDoor },
                    ].filter(s => s.available);

                    const activeSeries = allSeries.filter(s => seriesAxis[s.key]);
                    const leftSeries = activeSeries.filter(s => seriesAxis[s.key] === 'left');
                    const rightSeries = activeSeries.filter(s => seriesAxis[s.key] === 'right');

                    return (
                      <div className="flex flex-col gap-2 flex-1 min-h-0">
                        {/* Active series legend */}
                        {activeSeries.length > 0 ? (
                          <div className="flex flex-wrap gap-1.5">
                            {activeSeries.map(s => (
                              <button
                                key={s.key}
                                onClick={() => cycleSeries(s.key)}
                                className="flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium hover:opacity-70 transition-opacity"
                                title={`${s.label} (${seriesAxis[s.key] === 'left' ? 'L' : 'R'}) — click to switch axis`}
                              >
                                <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: s.color }} />
                                {s.label}
                                <span className="text-[8px] opacity-60 font-bold">{seriesAxis[s.key] === 'left' ? 'L' : 'R'}</span>
                              </button>
                            ))}
                          </div>
                        ) : (
                          <p className="text-[10px] text-muted-foreground">{t('performance.clickCardsToChart')}</p>
                        )}

                        {/* Unified chart with dual Y axes */}
                        {activeSeries.length > 0 && (
                          <div className="flex-1 min-h-0">
                          <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={telemetryData} onClick={(e) => {
                              if (e && e.activeTooltipIndex !== undefined) {
                                setCurrentIndex(e.activeTooltipIndex);
                              }
                            }}>
                              <defs>
                                {allSeries.map(s => (
                                  <linearGradient key={s.key} id={`${s.key}Gradient`} x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor={s.color} stopOpacity={0.2} />
                                    <stop offset="95%" stopColor={s.color} stopOpacity={0} />
                                  </linearGradient>
                                ))}
                              </defs>
                              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                              <XAxis dataKey="idx" type="number" domain={[0, telemetryData.length - 1]} tick={{ fontSize: 10 }} stroke="hsl(var(--muted-foreground))" tickFormatter={(i: number) => telemetryData[i]?.timeLabel ?? ''} minTickGap={60} />
                              {leftSeries.length > 0 && (
                                <YAxis
                                  yAxisId="left"
                                  tick={{ fontSize: 10 }}
                                  stroke={leftSeries.length === 1 ? leftSeries[0].color : 'hsl(var(--muted-foreground))'}
                                  width={45}
                                />
                              )}
                              {rightSeries.length > 0 && (
                                <YAxis
                                  yAxisId="right"
                                  orientation="right"
                                  tick={{ fontSize: 10 }}
                                  stroke={rightSeries.length === 1 ? rightSeries[0].color : 'hsl(var(--muted-foreground))'}
                                  width={45}
                                />
                              )}
                              <Tooltip
                                contentStyle={{
                                  backgroundColor: 'hsl(var(--popover))',
                                  border: '1px solid hsl(var(--border))',
                                  borderRadius: '8px',
                                  fontSize: '12px',
                                }}
                                labelFormatter={(i: number) => telemetryData[i]?.timeLabel ?? ''}
                              />
                              <ReferenceLine
                                x={currentIndex}
                                yAxisId={leftSeries.length > 0 ? 'left' : 'right'}
                                stroke="hsl(var(--foreground))"
                                strokeWidth={1.5}
                                strokeDasharray="4 3"
                              />
                              {activeSeries.map(s => (
                                <Area
                                  key={s.key}
                                  yAxisId={seriesAxis[s.key]}
                                  type="monotone"
                                  dataKey={s.key}
                                  stroke={s.color}
                                  fill={`url(#${s.key}Gradient)`}
                                  strokeWidth={2}
                                  dot={false}
                                  name={s.label}
                                  unit={s.unit}
                                />
                              ))}
                            </AreaChart>
                          </ResponsiveContainer>
                          </div>
                        )}
                      </div>
                    );
                  })()
                  }
                </CardContent>
              </Card>
            )}
            </div>

            {/* Raw Position Attributes (only visible with ?debug=1) */}
            {debugMode && attrEntries.length > 0 && (
              <Collapsible>
                <Card>
                  <CollapsibleTrigger asChild>
                    <CardHeader className="pb-3 cursor-pointer hover:bg-muted/50 transition-colors">
                      <CardTitle className="text-base flex items-center gap-2">
                        <Info className="h-5 w-5" />
                        {t('performance.rawAttributes')}
                        <Badge variant="secondary" className="ml-1">{attrEntries.length}</Badge>
                        <ChevronDown className="h-4 w-4 ml-auto transition-transform" />
                      </CardTitle>
                    </CardHeader>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <CardContent>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b text-left text-muted-foreground">
                              <th className="pb-2 pr-4 font-medium">{t('performance.attribute')}</th>
                              <th className="pb-2 font-medium">{t('performance.value')}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {attrEntries.map(([key, value]) => (
                              <tr key={key} className="border-b last:border-0">
                                <td className="py-1.5 pr-4 font-mono text-xs">{key}</td>
                                <td className="py-1.5 font-mono text-xs text-muted-foreground">{String(value)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </CardContent>
                  </CollapsibleContent>
                </Card>
              </Collapsible>
            )}
          </div>

          {/* Right column: Summary + Trips */}
          {showTripsPanel && (
          <div className="space-y-4 min-h-0 overflow-y-auto">
            {/* Trip Summary */}
            <Card>
              <CardHeader className="pb-2 px-4 pt-3">
                <CardTitle className="flex items-center gap-2 text-sm">
                  <Route className="h-4 w-4" />
                  {t('performance.tripSummary')}
                  {isLoadingData && <Loader2 className="h-3.5 w-3.5 animate-spin ml-1" />}
                </CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-3">
                <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                  <div>
                    <p className="text-[10px] text-muted-foreground">{t('performance.totalDistance')}</p>
                    <p className="text-sm font-semibold">{summary ? `${(summary.distance / 1000).toFixed(1)} km` : '—'}</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-muted-foreground">{t('performance.trips')}</p>
                    <p className="text-sm font-semibold">{tripTotals.tripCount}</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-muted-foreground">{t('performance.engineHours')}</p>
                    <p className="text-sm font-semibold">{summary?.engineHours ? formatTripDuration(summary.engineHours) : '—'}</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-muted-foreground">{t('performance.fuelConsumed')}</p>
                    <p className="text-sm font-semibold">{summary?.spentFuel ? `${summary.spentFuel.toFixed(1)} L` : '—'}</p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-[10px] text-muted-foreground">{t('performance.maxSpeed')}</p>
                    <p className="text-sm font-semibold">{summary ? `${Math.round(summary.maxSpeed * 1.852)} km/h` : '—'}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Trips & Stops Timeline */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-sm">
                  <Route className="h-4 w-4" />
                  {t('performance.trips')}
                  <Badge variant="secondary" className="ml-1 text-[10px]">{trips.length}</Badge>
                  {stops.length > 0 && (
                    <>
                      <MapPin className="h-3.5 w-3.5 ml-1 text-muted-foreground" />
                      <Badge variant="outline" className="text-[10px]">{stops.length}</Badge>
                    </>
                  )}
                  <Button
                    variant={fullRoute ? "default" : "outline"}
                    size="sm"
                    className="ml-auto h-6 px-2 text-[10px]"
                    onClick={() => {
                      setFullRoute(!fullRoute);
                      setSelectedTrip(null);
                      setSelectedStop(null);
                    }}
                    disabled={!selectedDeviceId || !dateRange?.from || !dateRange?.to}
                  >
                    {t('performance.fullRoute')}
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {isLoadingData ? (
                  <div className="flex items-center justify-center py-6">
                    <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                  </div>
                ) : timeline.length === 0 ? (
                  <div className="text-center py-6 text-muted-foreground">
                    <Route className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p className="text-xs">{t('performance.noTrips')}</p>
                  </div>
                ) : (
                  <div className="space-y-1.5 max-h-[350px] overflow-y-auto pr-1">
                    {timeline.map((item, index) => {
                      if (item.type === 'trip') {
                        const trip = item.data;
                        const isSelected = selectedTrip === trip;
                        return (
                          <div
                            key={`trip-${trip.startTime}-${index}`}
                            className={cn(
                              "border rounded-lg p-2.5 cursor-pointer transition-colors",
                              isSelected ? "border-primary bg-primary/5" : "hover:bg-muted/50"
                            )}
                            onClick={() => { setSelectedTrip(trip); setSelectedStop(null); setFullRoute(false); }}
                          >
                            <div className="flex items-center gap-2 text-sm">
                              <Route className="h-3 w-3 text-blue-500 shrink-0" />
                              {format(new Date(trip.startTime), 'MMM d, HH:mm', { locale: dateFnsLocale })} — {format(new Date(trip.endTime), 'HH:mm')}
                            </div>
                            <div className="flex items-center gap-2 mt-1 text-[11px] text-muted-foreground">
                              <span>{(trip.distance / 1000).toFixed(1)} km</span>
                              <span>{formatTripDuration(trip.duration)}</span>
                              <span>{Math.round(trip.maxSpeed * 1.852)} km/h</span>
                            </div>
                          </div>
                        );
                      } else {
                        const stop = item.data;
                        const isStopSelected = selectedStop === stop;
                        return (
                          <div
                            key={`stop-${stop.startTime}-${index}`}
                            className={cn(
                              "border border-dashed rounded-lg p-2.5 cursor-pointer transition-colors",
                              isStopSelected ? "border-red-400 bg-red-500/5" : "opacity-75 hover:bg-muted/50"
                            )}
                            onClick={() => { setSelectedStop(stop); setSelectedTrip(null); setFullRoute(false); }}
                          >
                            <div className="flex items-center gap-2 text-sm">
                              <MapPin className="h-3 w-3 text-red-400 shrink-0" />
                              {format(new Date(stop.startTime), 'MMM d, HH:mm', { locale: dateFnsLocale })} — {format(new Date(stop.endTime), 'HH:mm')}
                            </div>
                            {stop.address && (
                              <p className="text-[10px] text-muted-foreground mt-0.5 truncate" title={stop.address}>
                                {stop.address}
                              </p>
                            )}
                            <div className="flex items-center gap-2 mt-1 text-[11px] text-muted-foreground">
                              <span>{formatTripDuration(stop.duration)}</span>
                              {stop.engineHours > 0 && <span>⏱ {formatTripDuration(stop.engineHours)}</span>}
                            </div>
                          </div>
                        );
                      }
                    })}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Playback Controls (shown when trip selected) */}
            {(selectedTrip || selectedStop || fullRoute) && (
              <div className="grid grid-cols-3 gap-1.5">
                <Card>
                  <CardContent className="px-2 py-2 text-center">
                    <Navigation className="h-3 w-3 text-primary mx-auto" />
                    <p className="text-xs font-bold mt-0.5">{routePositions.length}</p>
                    <p className="text-[9px] text-muted-foreground">{t('route.totalPositions')}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="px-2 py-2 text-center">
                    <MapPin className="h-3 w-3 text-primary mx-auto" />
                    <p className="text-xs font-bold mt-0.5">
                      {selectedTrip ? `${(selectedTrip.distance / 1000).toFixed(1)} km` : selectedStop?.address ? selectedStop.address.substring(0, 15) + '…' : fullRoute && summary ? `${(summary.distance / 1000).toFixed(1)} km` : '—'}
                    </p>
                    <p className="text-[9px] text-muted-foreground">{selectedTrip || fullRoute ? t('route.totalDistance') : t('performance.stops')}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="px-2 py-2 text-center">
                    <Clock className="h-3 w-3 text-primary mx-auto" />
                    <p className="text-xs font-bold mt-0.5">{formatTripDuration(selectedTrip?.duration ?? selectedStop?.duration ?? 0)}</p>
                    <p className="text-[9px] text-muted-foreground">{t('route.totalDuration')}</p>
                  </CardContent>
                </Card>
              </div>
            )}
          </div>
          )}
        </div>
      </div>
      {/* Toggle trips panel button */}
      <Button
        variant="outline"
        size="icon"
        className="fixed bottom-4 right-4 z-40 h-9 w-9 rounded-full shadow-md"
        onClick={() => setShowTripsPanel(prev => !prev)}
        title={showTripsPanel ? t('performance.hideTrips') : t('performance.showTrips')}
      >
        {showTripsPanel ? <PanelRightClose className="h-4 w-4" /> : <PanelRightOpen className="h-4 w-4" />}
      </Button>
      </div>
    </DashboardLayout>
  );
};

export default PerformanceReport;
